import { useEffect, useState } from "react";

import {
  SHIPPING_METHOD_STANDARD,
  SHIPPING_METHOD_TWO_DAY,
} from "~/shared/sharedConstants";
import KickClient from "~/visits/utils/api.js";

const dateFormatter = new Intl.DateTimeFormat("en-US", {
  weekday: "short",
  month: "short",
  day: "numeric",
  timeZone: "UTC",
});

export function formatShippingDate(date) {
  const parsed = new Date(`${date}T00:00:00Z`);
  if (
    !/^\d{4}-\d{2}-\d{2}$/.test(date) ||
    !Number.isFinite(parsed.getTime()) ||
    parsed.toISOString().slice(0, 10) !== date
  ) {
    return "";
  }
  return dateFormatter.format(parsed);
}

// formatDeliveryDates renders the one date the server picked for the carrier
// (`deliveryDate`: the optimistic window start for FedEx Priority, the typical
// arrival for USPS Standard), never the start/end window it returns alongside.
// A quote without a valid `deliveryDate` -- unavailable, or a pre-v9 server
// answering during a rollout -- renders nothing and is never recorded as shown.
export function formatDeliveryDates(estimate) {
  if (!estimate?.available) return "";
  return formatShippingDate(estimate.deliveryDate);
}

export function formatDeliveryWindow(estimate) {
  const dates = formatDeliveryDates(estimate);
  return dates ? `Estimated arrival: ${dates}` : "";
}

const shippingMethods = [SHIPPING_METHOD_STANDARD, SHIPPING_METHOD_TWO_DAY];

// Refresh a quote shortly before it expires so the replacement arrives while
// the current dates are still valid and the badges never blink out. A quote
// that ends at a cutoff or at midnight can be shorter-lived than that; it is
// refreshed at expiry instead, and the dates may disappear for a moment then.
const REFRESH_LEAD_MS = 15000;

export default function useCheckoutShippingEstimates({
  enabled,
  visitID,
  orderType,
  usState,
  zip,
  poBox,
}) {
  const [result, setResult] = useState(null);
  const [requestState, setRequestState] = useState(null);
  const selection = { orderType, usState, zip, poBox };
  const selectionJSON = JSON.stringify(selection);
  const key = `${visitID}:${selectionJSON}`;
  // Transit estimates use the state, so they can load before ZIP entry.
  const eligible = enabled && !!visitID && !!usState;

  useEffect(() => {
    if (!eligible) return;
    let canceled = false;
    let timer;
    let expiryTimer;
    const controller = new AbortController();
    const client = KickClient.newAuthenticatedClient();
    const request = JSON.parse(selectionJSON);
    const path = `/visits/${visitID}/shipping-estimates`;

    async function refresh() {
      setRequestState({ key, pending: true });
      // The current quote stays on screen while its replacement loads; the
      // read below hides it the moment it expires or the destination changes.
      let refreshAt = Date.now() + 60000;
      try {
        const { data } = await client.post(path, request, {
          signal: controller.signal,
          timeout: 10000,
        });
        if (canceled) return;
        // Replace and expire the pair together, including across cutoffs.
        const expiresAt = Math.min(
          ...shippingMethods.map((method) =>
            Date.parse(data?.[method]?.expiresAt),
          ),
        );
        if (expiresAt > Date.now()) {
          const earlyRefreshAt = expiresAt - REFRESH_LEAD_MS;
          refreshAt =
            earlyRefreshAt - Date.now() >= REFRESH_LEAD_MS
              ? earlyRefreshAt
              : expiresAt;
          const estimates = Object.fromEntries(
            shippingMethods.map((method) => [
              method,
              formatDeliveryWindow(data[method]) ? data[method] : null,
            ]),
          );
          setResult({ key, estimates, expiresAt });
          // Nothing re-renders at the instant a quote expires, so drop it then
          // ourselves if the replacement has not arrived; a newer quote survives.
          clearTimeout(expiryTimer);
          expiryTimer = setTimeout(
            () => {
              if (canceled) return;
              setResult((current) =>
                current && current.expiresAt <= Date.now() ? null : current,
              );
            },
            Math.max(1, expiresAt - Date.now()),
          );
        }
      } catch {
        // The cleanup aborts an in-flight refresh when the destination changes
        // or the patient leaves the step. That is our own cancellation, not a
        // lookup failure, so it must not schedule a retry.
        if (canceled) return;
        // Estimates are advisory; a failed lookup must never block checkout.
      }
      if (!canceled) {
        setRequestState({ key, pending: false });
        timer = setTimeout(refresh, Math.max(1, refreshAt - Date.now()));
      }
    }

    // Avoid requests for every keystroke while the destination is being edited.
    timer = setTimeout(refresh, 300);
    return () => {
      canceled = true;
      controller.abort();
      clearTimeout(timer);
      clearTimeout(expiryTimer);
    };
  }, [eligible, visitID, selectionJSON, key]);

  const estimates =
    eligible && result?.key === key && result.expiresAt > Date.now()
      ? result.estimates
      : null;
  // True while an eligible selection has no usable quote yet: the first load,
  // a destination change, or a quote that expired before its refresh answered.
  // Failed, unavailable and malformed responses settle loading; the next retry
  // can show a spinner again. Keep valid dates visible during background refresh.
  const pending =
    eligible &&
    estimates === null &&
    (requestState?.key !== key || requestState.pending);

  useEffect(() => {
    if (!estimates) return;
    // Record only quotes rendered for the current destination and carrier.
    const client = KickClient.newAuthenticatedClient();
    for (const shipmentMethod of shippingMethods) {
      const estimate = estimates[shipmentMethod];
      if (!estimate) continue;
      client
        .post(`/visits/${visitID}/shipping-estimate/shown`, {
          ...JSON.parse(selectionJSON),
          shipmentMethod,
          estimate,
        })
        .catch(() => {});
    }
  }, [estimates, visitID, selectionJSON]);

  return {
    pending,
    estimates: estimates || {},
    deliveryWindows: Object.fromEntries(
      shippingMethods.map((method) => [
        method,
        formatDeliveryWindow(estimates?.[method]),
      ]),
    ),
  };
}
